import React, { useState } from 'react';
import { 
  FiUsers, 
  FiCalendar, 
  FiCheckCircle, 
  FiClock, 
  FiAward, 
  FiLayers, 
  FiLink, 
  FiPlay, 
  FiSearch, 
  FiShield, 
  FiChevronRight,
  FiTrendingUp
} from 'react-icons/fi';

const colors = {
  orange: '#f35018',
  blue: '#2f3194',
  deepNavy: '#0b0c26',
  surfaceDark: '#16183e',
  cyan: '#63decc',
  white: '#ffffff',
  textMain: '#0f1137',
  bgLight: '#f4f6fc',
  muted: '#64748b',
  border: '#e2e8f0',
  success: '#10b981',
  danger: '#ef4444',
  gold: '#fbbf24',
};

export default function HostAnalyticsTab({ intelligenceData, loading, onRefresh }) {
  const { hosts = [], events = [], liveSessions = [], selfPacedQuizzes = [] } = intelligenceData || {};
  const [selectedHostId, setSelectedHostId] = useState(hosts[0]?.id || null);
  const [hostSearch, setHostSearch] = useState('');

  // 1. Safe Active Host Resolution
  const activeHost = hosts.find((h) => Number(h.id) === Number(selectedHostId)) || hosts[0];

  // 2. Safe Event & Session Filtering
  const hostEvents = events.filter((e) => Number(e.user_id) === Number(activeHost?.id));
  const hostLiveSessions = liveSessions.filter((ls) => Number(ls.user_id) === Number(activeHost?.id));
  const hostSelfPaced = selfPacedQuizzes.filter((sp) => Number(sp.user_id) === Number(activeHost?.id));

  // Cumulative participant reach calculation
  const totalLiveParticipants = hostLiveSessions.reduce((acc, curr) => acc + Number(curr.participant_count || 0), 0);
  const totalSelfPacedParticipants = hostSelfPaced.reduce((acc, curr) => acc + Number(curr.participant_count || 0), 0);
  const totalEngagedPlayers = totalLiveParticipants + totalSelfPacedParticipants;

  const filteredHosts = hosts.filter((h) => {
    const q = hostSearch.toLowerCase();
    return (
      (h.name && h.name.toLowerCase().includes(q)) ||
      (h.email && h.email.toLowerCase().includes(q)) ||
      (h.mobile && h.mobile.includes(q))
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* INJECT SLEEK MODERN SCROLLBAR STYLES */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.03);
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(47, 49, 148, 0.2);
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(243, 80, 24, 0.4);
        }
      `}</style>
      
      {/* 1. TOP EXECUTIVE REPORTING KPI TILES */}
      <div style={styles.kpiRow}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiIcon(colors.blue)}>
            <FiUsers size={22} color={colors.blue} />
          </div>
          <div>
            <span style={styles.kpiLabel}>TOTAL ENGAGED LEARNERS</span>
            <h3 style={styles.kpiNumber}>{totalEngagedPlayers.toLocaleString()}</h3>
            <span style={styles.kpiSub}>Across Live PINs &amp; Self-Paced Links</span>
          </div>
        </div>

        <div style={styles.kpiCard}>
          <div style={styles.kpiIcon(colors.orange)}>
            <FiLayers size={22} color={colors.orange} />
          </div>
          <div>
            <span style={styles.kpiLabel}>ACTIVE QUIZ EVENTS</span>
            <h3 style={styles.kpiNumber}>{hostEvents.length}</h3>
            <span style={styles.kpiSub}>Created by {activeHost?.name || 'Host'}</span>
          </div>
        </div>

        <div style={styles.kpiCard}>
          <div style={styles.kpiIcon(colors.success)}>
            <FiPlay size={22} color={colors.success} />
          </div>
          <div>
            <span style={styles.kpiLabel}>LIVE SESSIONS COMPLETED</span>
            <h3 style={{ ...styles.kpiNumber, color: colors.success }}>{hostLiveSessions.length}</h3>
            <span style={styles.kpiSub}>{totalLiveParticipants} Live Submissions</span>
          </div>
        </div>

        <div style={styles.kpiCard}>
          <div style={styles.kpiIcon('#0284c7')}>
            <FiLink size={22} color="#0284c7" />
          </div>
          <div>
            <span style={styles.kpiLabel}>SELF-PACED QUIZZES</span>
            <h3 style={{ ...styles.kpiNumber, color: '#0284c7' }}>{hostSelfPaced.length}</h3>
            <span style={styles.kpiSub}>{totalSelfPacedParticipants} Asynchronous Attempts</span>
          </div>
        </div>
      </div>

      {/* 2. SPLIT WORKBENCH: DUAL-SCROLL PANELS */}
      <div style={styles.workbenchGrid}>
        
        {/* LEFT PANEL: SELECT HOST PROFILE */}
        <div style={styles.sidebarPanel}>
          <div style={styles.panelHeader}>
            <h4 style={styles.panelTitle}>Select Host Profile</h4>
            <div style={styles.searchWrap}>
              <FiSearch size={14} color={colors.muted} />
              <input 
                type="text"
                placeholder="Filter hosts..."
                value={hostSearch}
                onChange={(e) => setHostSearch(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          {/* Left Scrollable List */}
          <div className="custom-scrollbar" style={styles.hostListScroll}>
            {filteredHosts.map((h) => {
              const isSelected = Number(h.id) === Number(activeHost?.id);
              const tierColor = h.plan_tier === 'gold' ? '#b45309' : h.plan_tier === 'silver' ? colors.orange : colors.blue;
              const tierBg = h.plan_tier === 'gold' ? '#fef3c7' : h.plan_tier === 'silver' ? '#fff7ed' : '#eff6ff';

              return (
                <div 
                  key={h.id}
                  onClick={() => setSelectedHostId(h.id)}
                  style={{
                    ...styles.hostListItem,
                    backgroundColor: isSelected ? '#fffaf8' : '#ffffff',
                    borderLeft: isSelected ? `4px solid ${colors.orange}` : '4px solid transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={{ fontWeight: '800', fontSize: '13px', color: isSelected ? colors.orange : colors.textMain }}>
                      {h.name}
                    </span>
                    <span style={{ ...styles.tierPill, backgroundColor: tierBg, color: tierColor }}>
                      {(h.plan_tier || 'trial').toUpperCase()}
                    </span>
                  </div>
                  <span style={{ fontSize: '11px', color: colors.muted, display: 'block', marginTop: '2px' }}>
                    {h.email}
                  </span>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '10px', color: colors.muted }}>
                    <span>ID: #{h.id}</span>
                    <span>{h.mobile || 'No Mobile'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT PANEL: QUIZ EVENTS & DELIVERY SESSIONS */}
        <div style={styles.contentPanel}>
          
          {/* Fixed Sticky Header for Active Host */}
          {activeHost && (
            <div style={styles.hostBanner}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: colors.textMain }}>
                    {activeHost.name}
                  </h3>
                  <span style={{ 
                    fontSize: '11px', 
                    fontWeight: '800', 
                    padding: '2px 8px', 
                    borderRadius: '6px',
                    backgroundColor: activeHost.isApprove === 1 ? '#ecfdf5' : '#fff1f2',
                    color: activeHost.isApprove === 1 ? colors.success : colors.danger
                  }}>
                    {activeHost.isApprove === 1 ? 'Approved Host' : 'Pending Authorization'}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: colors.muted }}>
                  {activeHost.email} &bull; {activeHost.mobile} &bull; Joined {new Date(activeHost.created_at).toLocaleDateString()}
                </p>
              </div>

              <div style={styles.quotaPillBox}>
                <span style={{ fontSize: '10px', fontWeight: '800', color: colors.muted, textTransform: 'uppercase' }}>
                  Event Quota Utilization
                </span>
                <strong style={{ fontSize: '14px', color: colors.blue }}>
                  {activeHost.used_event_quizzes || 0} / {activeHost.allowed_event_quizzes || 0} Quizzes Used
                </strong>
              </div>
            </div>
          )}

          {/* Right Scrollable Events & Sessions Body */}
          <div className="custom-scrollbar" style={styles.contentScrollBody}>
            {activeHost ? (
              <div>
                <h4 style={styles.sectionHeading}>
                  Quiz Events &amp; Delivery Sessions ({hostEvents.length})
                </h4>

                {hostEvents.length === 0 ? (
                  <div style={styles.emptyCard}>
                    <p style={{ margin: 0, color: colors.muted, fontSize: '13px' }}>
                      This host has not initialized any quiz events yet.
                    </p>
                  </div>
                ) : (
                  hostEvents.map((event) => {
                    const eventLive = hostLiveSessions.filter((ls) => Number(ls.event_id) === Number(event.id));
                    const eventSelfPaced = hostSelfPaced.filter((sp) => Number(sp.event_id) === Number(event.id));

                    return (
                      <div key={event.id} style={styles.eventCard}>
                        {/* Event Card Header */}
                        <div style={styles.eventCardHeader}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {event.file ? (
                              <img 
                                src={event.file} 
                                alt="Event" 
                                style={{ width: '38px', height: '38px', borderRadius: '8px', objectFit: 'cover' }} 
                              />
                            ) : (
                              <div style={styles.eventAvatar}>
                                {event.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: colors.textMain }}>
                                  {event.name}
                                </h5>
                                {event.is_locked === 1 && (
                                  <span style={styles.lockBadge}>🔒 LOCKED</span>
                                )}
                              </div>
                              <span style={{ fontSize: '11px', color: colors.muted }}>
                                Event ID: #{event.id} &bull; Question Bank: {event.question_count} Qs &bull; Created {new Date(event.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Session Grids */}
                        <div style={{ padding: '16px' }}>
                          <span style={styles.subSectionTitle}>Active / Completed Deployments</span>
                          
                          <div style={styles.deploymentGrid}>
                            
                            {/* Live Pin Deployments */}
                            <div style={styles.deploymentCol}>
                              <span style={styles.colHeader}>🎮 Live Matches (PIN Broadcast)</span>
                              {eventLive.length === 0 ? (
                                <span style={styles.noneText}>No live PIN matches generated.</span>
                              ) : (
                                eventLive.map((ls) => (
                                  <div key={ls.id} style={styles.sessionItem}>
                                    <div>
                                      <strong style={{ fontSize: '13px', color: colors.orange }}>PIN: {ls.room_pin}</strong>
                                      <span style={{ display: 'block', fontSize: '10px', color: colors.muted }}>
                                        {new Date(ls.created_at).toLocaleDateString()} &bull; Status: {ls.status.toUpperCase()}
                                      </span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <span style={styles.playerCountBadge}>
                                        👥 {ls.participant_count} Players
                                      </span>
                                      <span style={{ display: 'block', fontSize: '10px', color: colors.muted, marginTop: '2px' }}>
                                        High: {ls.top_score} pts
                                      </span>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>

                            {/* Self Paced Deployments */}
                            <div style={styles.deploymentCol}>
                              <span style={styles.colHeader}>⚡ Self-Paced Links (8-Char Code)</span>
                              {eventSelfPaced.length === 0 ? (
                                <span style={styles.noneText}>No self-paced link quotas deployed.</span>
                              ) : (
                                eventSelfPaced.map((sp) => (
                                  <div key={sp.id} style={styles.sessionItem}>
                                    <div>
                                      <strong style={{ fontSize: '13px', color: '#0284c7' }}>CODE: {sp.quiz_code}</strong>
                                      <span style={{ display: 'block', fontSize: '10px', color: colors.muted }}>
                                        Expires: {sp.expires_at ? new Date(sp.expires_at).toLocaleDateString() : 'No Limit'}
                                      </span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <span style={{ ...styles.playerCountBadge, backgroundColor: 'rgba(2, 132, 199, 0.1)', color: '#0284c7' }}>
                                        👥 {sp.participant_count} Finished
                                      </span>
                                      <span style={{ display: 'block', fontSize: '10px', color: colors.muted, marginTop: '2px' }}>
                                        High: {sp.top_score} pts
                                      </span>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>

                          </div>
                        </div>

                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div style={styles.emptyCard}>Select a host from the left to view analytics.</div>
            )}
          </div>
        </div>

      </div>

    </div>
  );
}

const styles = {
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '14px',
  },
  kpiCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '18px',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 2px 8px rgba(15, 17, 55, 0.02)',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  kpiIcon: (color) => ({
    width: '46px',
    height: '46px',
    borderRadius: '12px',
    backgroundColor: `${color}15`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  kpiLabel: {
    fontSize: '10px',
    fontWeight: '800',
    color: colors.muted,
    letterSpacing: '0.6px',
    display: 'block',
  },
  kpiNumber: {
    margin: '2px 0',
    fontSize: '24px',
    fontWeight: '900',
    color: colors.textMain,
  },
  kpiSub: {
    fontSize: '11px',
    color: colors.muted,
  },
  workbenchGrid: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '18px',
    height: 'calc(100vh - 270px)',
    minHeight: '620px',
    alignItems: 'stretch',
  },
  sidebarPanel: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '16px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: '#ffffff',
    flexShrink: 0,
  },
  panelTitle: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    fontWeight: '800',
    color: colors.textMain,
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.bgLight,
    padding: '6px 10px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
  },
  searchInput: {
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: '12px',
    width: '100%',
    marginLeft: '6px',
  },
  hostListScroll: {
    flex: 1,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(47, 49, 148, 0.2) transparent',
  },
  hostListItem: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  tierPill: {
    fontSize: '9px',
    fontWeight: '800',
    padding: '1px 6px',
    borderRadius: '4px',
  },
  contentPanel: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxShadow: '0 4px 12px rgba(15, 17, 55, 0.03)',
    overflow: 'hidden',
  },
  hostBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '18px 22px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: '#ffffff',
    flexShrink: 0,
  },
  contentScrollBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '22px',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(243, 80, 24, 0.25) transparent',
  },
  quotaPillBox: {
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionHeading: {
    fontSize: '14px',
    fontWeight: '800',
    color: colors.textMain,
    margin: '0 0 14px 0',
  },
  eventCard: {
    borderRadius: '12px',
    border: `1px solid ${colors.border}`,
    marginBottom: '14px',
    overflow: 'hidden',
    backgroundColor: '#fafbfc',
  },
  eventCardHeader: {
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    borderBottom: `1px solid ${colors.border}`,
  },
  eventAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '8px',
    backgroundColor: 'rgba(47, 49, 148, 0.1)',
    color: colors.blue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '800',
    fontSize: '14px',
  },
  lockBadge: {
    fontSize: '9px',
    fontWeight: '800',
    color: colors.danger,
    backgroundColor: '#fee2e2',
    padding: '1px 5px',
    borderRadius: '4px',
  },
  subSectionTitle: {
    fontSize: '11px',
    fontWeight: '800',
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    display: 'block',
    marginBottom: '10px',
  },
  deploymentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '12px',
  },
  deploymentCol: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '12px',
    border: `1px solid ${colors.border}`,
  },
  colHeader: {
    fontSize: '12px',
    fontWeight: '800',
    color: colors.textMain,
    display: 'block',
    marginBottom: '8px',
  },
  sessionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    marginBottom: '6px',
    border: `1px solid ${colors.border}`,
  },
  playerCountBadge: {
    fontSize: '10px',
    fontWeight: '800',
    backgroundColor: 'rgba(243, 80, 24, 0.1)',
    color: colors.orange,
    padding: '2px 6px',
    borderRadius: '4px',
  },
  noneText: {
    fontSize: '11px',
    color: colors.muted,
    fontStyle: 'italic',
  },
  emptyCard: {
    padding: '30px',
    textAlign: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: '10px',
    border: `1px dashed ${colors.border}`,
  },
};